import { useState, useEffect } from 'react';
import { Bookmark, ChevronDown, Trash2, Star, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchSavedFilters,
  createSavedFilterApi,
  deleteSavedFilterApi,
} from '@/hooks/useTickets';
import type { SavedFilterItem } from '@uniticket/shared';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useAuth } from '@/hooks/useAuth';

interface SavedFiltersDropdownProps {
  onLoad: (config: Record<string, unknown>) => void;
  onGetCurrentConfig: () => Record<string, unknown>;
}

export function SavedFiltersDropdown({ onLoad, onGetCurrentConfig }: SavedFiltersDropdownProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState<SavedFilterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [saveDefault, setSaveDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  async function loadFilters() {
    try {
      setLoading(true);
      const res = await fetchSavedFilters();
      setFilters(res.data);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) loadFilters();
  }, [open]);

  async function handleSave() {
    if (!saveName.trim()) return;
    try {
      setSaving(true);
      const config = onGetCurrentConfig();
      await createSavedFilterApi({
        name: saveName.trim(),
        filterConfig: config,
        isShared: saveShared,
        isDefault: saveDefault,
      });
      toast.success(`Filter "${saveName.trim()}" saved`);
      setSaveName('');
      setSaveShared(false);
      setSaveDefault(false);
      setShowSaveForm(false);
      loadFilters();
    } catch {
      toast.error('Failed to save filter');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string, name: string) {
    try {
      await deleteSavedFilterApi(id);
      setFilters((prev) => prev.filter((f) => f.id !== id));
      toast.success(`Filter "${name}" deleted`);
    } catch {
      toast.error('Failed to delete filter');
    }
  }

  function handleLoad(filter: SavedFilterItem) {
    onLoad(filter.filterConfig);
    setOpen(false);
    toast.success(`Filter "${filter.name}" applied`);
  }

  const myFilters = filters.filter((f) => f.userId === user?.id);
  const sharedFilters = filters.filter((f) => f.userId !== user?.id && f.isShared);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
          <Bookmark className="w-4 h-4" />
          Saved Filters
          <ChevronDown className="w-3 h-3 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        ) : (
          <div>
            {/* My Filters */}
            {myFilters.length > 0 && (
              <div className="p-2">
                <p className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">My Filters</p>
                {myFilters.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-slate-50 group"
                  >
                    <button
                      onClick={() => handleLoad(f)}
                      className="flex items-center gap-2 text-sm text-slate-700 hover:text-brand-rose transition-colors flex-1 text-left"
                    >
                      {f.isDefault && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                      {f.isShared && <Share2 className="w-3 h-3 text-blue-500" />}
                      <span className="truncate">{f.name}</span>
                    </button>
                    <button
                      onClick={() => handleDelete(f.id, f.name)}
                      className="p-1 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Shared Filters */}
            {sharedFilters.length > 0 && (
              <div className="p-2 border-t border-slate-100">
                <p className="px-2 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">Shared</p>
                {sharedFilters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => handleLoad(f)}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-sm text-slate-700 hover:text-brand-rose hover:bg-slate-50 rounded-md transition-colors text-left"
                  >
                    <Share2 className="w-3 h-3 text-blue-500" />
                    <span className="truncate">{f.name}</span>
                  </button>
                ))}
              </div>
            )}

            {myFilters.length === 0 && sharedFilters.length === 0 && !showSaveForm && (
              <div className="px-4 py-6 text-center">
                <p className="text-sm text-slate-500">No saved filters yet</p>
              </div>
            )}

            {/* Save form */}
            <div className="border-t border-slate-200 p-2">
              {showSaveForm ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="Filter name..."
                    className="w-full px-3 py-1.5 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                  />
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveShared}
                        onChange={(e) => setSaveShared(e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Share with team
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveDefault}
                        onChange={(e) => setSaveDefault(e.target.checked)}
                        className="rounded border-slate-300"
                      />
                      Set as default
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setShowSaveForm(false); setSaveName(''); }}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={!saveName.trim() || saving}
                      className="flex-1 px-3 py-1.5 text-xs font-semibold text-white rounded-md bg-gradient-to-r from-brand-rose to-brand-orange hover:shadow-md transition-all disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowSaveForm(true)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium text-brand-rose hover:bg-brand-rose/5 rounded-md transition-colors"
                >
                  <Bookmark className="w-3.5 h-3.5" />
                  Save current filters...
                </button>
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
